import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { type CaregiverHistogramBucket } from '@libraries/typescript/api_client';

type CaregiverHistogramProps = {
    readonly buckets: Array<CaregiverHistogramBucket>;
    // Fires with the clicked bucket's suggested-caregiver count for a runs drill.
    readonly onBucketClick?: (point: HistogramPoint) => void;
};

type HistogramPoint = {
    readonly label: string;
    readonly count: number;
    readonly suggestedCount: number;
    readonly isZeroBucket: boolean;
};

type HistogramTooltipPayload = {
    readonly payload?: HistogramPoint;
};

function HistogramTooltip({
    active,
    payload,
}: {
    readonly active?: boolean;
    readonly payload?: Array<HistogramTooltipPayload>;
}) {
    if (!active || !payload?.[0]?.payload) {
        return null;
    }
    const point = payload[0].payload;
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="font-medium">
                {point.label} suggested caregivers
            </div>
            <div className="text-muted-foreground">
                {point.count} outreaches
            </div>
        </div>
    );
}

export function CaregiverHistogram({
    buckets,
    onBucketClick,
}: CaregiverHistogramProps) {
    const data: Array<HistogramPoint> = buckets.map((bucket) => ({
        label: String(bucket.suggested_caregiver_count),
        count: bucket.run_count,
        suggestedCount: bucket.suggested_caregiver_count,
        isZeroBucket: bucket.suggested_caregiver_count === 0,
    }));
    if (data.length === 0) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center rounded-md border text-sm">
                No data to plot.
            </div>
        );
    }

    return (
        <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid
                        stroke="var(--border)"
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <RechartsTooltip content={<HistogramTooltip />} />
                    <Bar
                        dataKey="count"
                        isAnimationActive={false}
                        cursor={onBucketClick ? 'pointer' : undefined}
                        onClick={
                            onBucketClick
                                ? (entry: {
                                      readonly payload?: HistogramPoint;
                                  }) => {
                                      if (entry.payload !== undefined) {
                                          onBucketClick(entry.payload);
                                      }
                                  }
                                : undefined
                        }
                    >
                        {data.map((point) => (
                            <Cell
                                key={point.label}
                                fill={
                                    point.isZeroBucket
                                        ? 'var(--destructive)'
                                        : 'var(--chart-1)'
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
